import { Button } from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Loader from "../Layout/Loader";
import DashboardTableScroll from "../shared/DashboardTableScroll";
import { server, backend_url } from "../../server";

const STATUS_BADGES = {
  pending: { className: "bg-amber-100 text-amber-700", label: "Pending" },
  verified: { className: "bg-emerald-100 text-emerald-700", label: "Approved" },
  rejected: { className: "bg-rose-100 text-rose-700", label: "Rejected" },
};

const AllVeteranVerifications = () => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all"); // pending | rejected | verified | all
  const [searchQuery, setSearchQuery] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null); // raw user doc
  const [rejectReason, setRejectReason] = useState("");

  const fetchVerifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await axios.get(
        `${server}/user/admin-veteran-verifications?status=${encodeURIComponent(statusFilter)}`,
        { withCredentials: true }
      );
      setRequests(data?.users || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load veteran verifications.");
      setRequests([]);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchVerifications();
  }, [fetchVerifications]);

  const rows = useMemo(() => {
    return (requests || []).map((u) => ({
      id: u._id,
      name: u.name || "",
      email: u.email || "",
      phoneNumber: u.phoneNumber || "",
      status: u.veteranVerificationStatus || "none",
      proofImage: u.veteranProofImage || null,
      rejectionReason: u.veteranRejectionReason || "",
      createdAt: u.createdAt || null,
      raw: u,
    }));
  }, [requests]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const combined = [r.name, r.email, r.phoneNumber, r.status]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return combined.includes(q);
    });
  }, [rows, searchQuery]);

  const formatDate = (value) => {
    if (!value) return "—";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleDateString();
    } catch {
      return "—";
    }
  };

  const renderStatusBadge = (status) => {
    const s = String(status || "none").toLowerCase();
    const badge = STATUS_BADGES[s] || null;
    if (!badge) {
      return (
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
          —
        </span>
      );
    }

    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${badge.className}`}>
        {badge.label}
      </span>
    );
  };

  const openModalForRow = useCallback((row) => {
    const raw = row?.raw || row;
    setSelectedUser(raw);
    setRejectReason(String(row?.rejectionReason || raw?.veteranRejectionReason || "").trim());
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedUser(null);
    setRejectReason("");
  }, []);

  const updateVeteranStatus = useCallback(
    async (userId, nextStatus, reason) => {
      if (!userId) return;
      try {
        setUpdatingId(userId);
        await axios.put(
          `${server}/user/admin-user/${userId}`,
          {
            veteranVerificationStatus: nextStatus,
            veteranRejectionReason: nextStatus === "rejected" ? reason : "",
          },
          { withCredentials: true }
        );
        toast.success(nextStatus === "verified" ? "Veteran approved." : "Veteran rejected.");
        await fetchVerifications();
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to update veteran verification.");
      } finally {
        setUpdatingId(null);
      }
    },
    [fetchVerifications]
  );

  const handleApprove = useCallback(async () => {
    if (!selectedUser?._id) return;
    if (String(selectedUser.veteranVerificationStatus) !== "pending") return;
    if (updatingId === selectedUser._id) return;
    await updateVeteranStatus(selectedUser._id, "verified", "");
    closeModal();
  }, [selectedUser, updatingId, updateVeteranStatus, closeModal]);

  const handleReject = useCallback(async () => {
    if (!selectedUser?._id) return;
    if (String(selectedUser.veteranVerificationStatus) !== "pending") return;
    const reason = (rejectReason || "").trim();
    if (!reason) {
      toast.error("Please enter a rejection reason.");
      return;
    }
    if (updatingId === selectedUser._id) return;
    await updateVeteranStatus(selectedUser._id, "rejected", reason);
    closeModal();
  }, [selectedUser, rejectReason, updatingId, updateVeteranStatus, closeModal]);

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "User",
        minWidth: 240,
        flex: 1.2,
        renderCell: (params) => <span className="font-medium">{params.value}</span>,
      },
      { field: "email", headerName: "Email", minWidth: 220, flex: 1.2 },
      { field: "phoneNumber", headerName: "Phone", minWidth: 140, flex: 0.7 },
      {
        field: "status",
        headerName: "Status",
        minWidth: 160,
        flex: 0.8,
        sortable: false,
        renderCell: (params) => renderStatusBadge(params.value),
      },
      {
        field: "proofImage",
        headerName: "Proof",
        minWidth: 160,
        flex: 0.7,
        sortable: false,
        renderCell: (params) => {
          if (!params.value) return <span className="text-slate-400">—</span>;
          return (
            <img
              src={`${backend_url}${params.value}`}
              alt="Veteran proof"
              className="h-12 w-12 object-contain rounded border border-slate-200"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Submitted",
        minWidth: 130,
        flex: 0.5,
        sortable: true,
        valueFormatter: ({ value }) => formatDate(value),
      },
    ],
    []
  );

  if (isLoading && rows.length === 0) {
    return <Loader />;
  }

  return (
    <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold text-slate-900">Veteran verification</h2>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-700">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-transparent"
            >
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
              <option value="verified">Approved</option>
              <option value="all">All</option>
            </select>
          </div>

          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search users..."
            className="w-full max-w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20 sm:w-56 sm:max-w-md"
          />
        </div>
      </div>

      <div className="p-4 pt-0">
        <DashboardTableScroll>
          <div className="min-w-[880px]">
            <DataGrid
              rows={filteredRows}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 20, 50]}
              disableSelectionOnClick
              autoHeight
              loading={isLoading && rows.length === 0}
              onRowClick={(params) => openModalForRow(params.row)}
              sx={{
                border: "none",
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #e2e8f0",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#1e293b",
                  borderBottom: "2px solid #334155",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  color: "#ffffff",
                },
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f8fafc",
                },
              }}
            />
          </div>
        </DashboardTableScroll>
      </div>

      {isModalOpen && selectedUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="w-full max-w-3xl bg-white rounded-lg shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Veteran verification</h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600 transition"
                onClick={closeModal}
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-600">Proof</p>
                  {selectedUser.veteranProofImage ? (
                    <img
                      src={`${backend_url}${selectedUser.veteranProofImage}`}
                      alt="Veteran proof"
                      className="w-full max-h-[420px] object-contain rounded border border-slate-200"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <p className="text-sm text-slate-500">No proof on file.</p>
                  )}
                  {String(selectedUser.veteranVerificationStatus) === "rejected" &&
                    (selectedUser.veteranRejectionReason || "").trim() && (
                      <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                        <p className="font-semibold">Rejection reason</p>
                        <p className="mt-1 whitespace-pre-wrap">
                          {selectedUser.veteranRejectionReason}
                        </p>
                      </div>
                    )}
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-slate-600">User</p>
                    <p className="text-base font-semibold text-slate-900">
                      {selectedUser.name || "—"}
                    </p>
                    <p className="text-sm text-slate-600">
                      Email: <span className="font-medium text-slate-900">{selectedUser.email || "—"}</span>
                    </p>
                    <p className="text-sm text-slate-600">
                      Phone: <span className="font-medium text-slate-900">{selectedUser.phoneNumber || "—"}</span>
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-slate-600">Status</p>
                    <div className="mt-2">{renderStatusBadge(selectedUser.veteranVerificationStatus)}</div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-slate-600">Submitted</p>
                    <p className="text-sm text-slate-700">
                      {formatDate(selectedUser.createdAt)}
                    </p>
                  </div>

                  {String(selectedUser.veteranVerificationStatus) !== "pending" && (
                    <p className="text-sm text-amber-700 font-medium">
                      This submission is not pending, so you can only view it.
                    </p>
                  )}
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-900">Decision</h4>

                {String(selectedUser.veteranVerificationStatus) === "pending" ? (
                  <>
                    <label className="block">
                      <span className="text-sm font-medium text-slate-700">
                        Rejection reason (required when rejecting)
                      </span>
                      <textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={4}
                        className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#38513b] focus:border-[#38513b] resize-none"
                        placeholder="Enter reason for rejection..."
                      />
                    </label>

                    <div className="flex flex-wrap gap-2 justify-end pt-2">
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={updatingId === selectedUser._id}
                        onClick={handleApprove}
                      >
                        {updatingId === selectedUser._id ? "Updating…" : "Approve"}
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        disabled={updatingId === selectedUser._id}
                        onClick={handleReject}
                      >
                        {updatingId === selectedUser._id ? "Updating…" : "Reject"}
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-600">
                    Awaiting a new submission after rejection, or already approved.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllVeteranVerifications;

